import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_DATA_CONTROL_RISK_SCORE } from "../DataControlConstans"; // adjust the path to where this file lives
import { makeStyles, Tooltip } from "@material-ui/core";

// score sweeps left (0) -> right (100); gradient is green (safe, left) to
// red (danger, right), so a high score visually lands in the red zone.

// "12.40" -> 12.4, 7 -> 7 (keeps up to 1 decimal, drops trailing zeros)
const fmtScore = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(1)) : 0;
};

const useGaugeInfoStyles = makeStyles(() => ({
  tooltip: {
    backgroundColor: "#fff",
    color: "#1f2733",
    fontSize: "12px",
    lineHeight: 1.45,
    maxWidth: 260,
    padding: "10px 12px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.16)",
    border: "1px solid #e0e0e0",
    borderRadius: 8,
  },
  arrow: {
    color: "#fff",
    "&::before": { border: "1px solid #e0e0e0" },
  },
}));

export function RiskGauge({ score }) {
  const clamped = Math.max(0, Math.min(100, score));
  const cx = 100;
  const cy = 100;
  const r = 80;

  const thetaRad = ((100 - clamped) / 100) * Math.PI;
  const markerX = cx + r * Math.cos(thetaRad);
  const markerY = cy - r * Math.sin(thetaRad);

  return (
    <svg viewBox="0 0 200 112" className="fsc-gauge__svg">
      <defs>
        <linearGradient id="fscGaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#2fb56a" />
          <stop offset="50%" stopColor="#f5a623" />
          <stop offset="100%" stopColor="#e0403f" />
        </linearGradient>
      </defs>

      <path
        d="M20 100 A80 80 0 0 1 180 100"
        fill="none"
        stroke="url(#fscGaugeGradient)"
        strokeWidth="14"
        strokeLinecap="round"
      />

      <line
        x1={cx}
        y1={cy}
        x2={markerX}
        y2={markerY}
        className="fsc-gauge__needle"
      />

      <circle cx={cx} cy={cy} r="6" className="fsc-gauge__pivot" />
      <circle cx={markerX} cy={markerY} r="7" className="fsc-gauge__marker" />
    </svg>
  );
}

export function RiskOverviewHeader() {
  const [trend, setTrend] = useState(null);

  const gaugeInfoClasses = useGaugeInfoStyles();
  const dispatch = useDispatch();

  // 0-100 score calculated by the backend: /tenant/dataControl/riskScore
  const riskScore = useSelector((state) => state.dataControlReducer?.riskScore);

  useEffect(() => {
    dispatch({ type: GET_DATA_CONTROL_RISK_SCORE });
  }, [dispatch]);

  const summaryData = useSelector(
    (state) => state.dataControlReducer?.dashboardSummary,
  );

  // fallback only, used until the riskScore API has answered
  const riskScoreMap = {
    CRITICAL: 100,
    HIGH: 75,
    MEDIUM: 50,
    LOW: 25,
    SAFE: 0,
  };

  const hasApiScore = typeof riskScore?.totalScore === "number";

  const overview = useMemo(() => {
    const risk = summaryData?.overAllRisk || "SAFE";

    return {
      score: hasApiScore
        ? fmtScore(riskScore.totalScore)
        : (riskScoreMap[risk] ?? 0),
      maxScore: riskScore?.maxScore ?? 100,
      parts: {
        publicFiles: riskScore?.publicFiles,
        restrictUsers: riskScore?.restrictUsers,
        orgWide: riskScore?.orgWideBlocking,
      },
      riskLabel: risk,
      tone: risk.toLowerCase(),
      totalPending: summaryData?.totalRisksOpen ?? 0,
      description: summaryData?.description ?? "",
      recommendation: summaryData?.recommendation ?? "",
      reasoning: summaryData?.reasoning ?? "",
      bySeverity: {
        Critical: summaryData?.criticalOpen ?? 0,
        High: summaryData?.highOpen ?? 0,
        Medium: summaryData?.mediumOpen ?? 0,
        Low: summaryData?.lowOpen ?? 0,
      },
    };
  }, [summaryData, riskScore, hasApiScore]);

  const prevScoreRef = useRef(null);

  useEffect(() => {
    if (!summaryData) return;

    if (prevScoreRef.current === null) {
      prevScoreRef.current = overview.score;
      return;
    }

    if (prevScoreRef.current === overview.score) return;

    const delta = prevScoreRef.current - overview.score;
    setTrend(
      delta > 0
        ? { direction: "down", delta }
        : { direction: "up", delta: Math.abs(delta) },
    );
    prevScoreRef.current = overview.score;
  }, [overview.score, summaryData]);

  // Defined after `overview` — it reads overview.reasoning and the score parts.
  const part = (p, label, fallbackMax) => (
    <span>
      <b>
        {p ? fmtScore(p.score) : "-"}/{p?.max ?? fallbackMax}
      </b>{" "}
      {label}
    </span>
  );

  const scoreExplainer = (
    <div className="fsc-gauge-tip">
      <p className="fsc-gauge-tip__lead">
        Out of <strong>100</strong>, higher is riskier. It adds up three parts:
      </p>

      <div className="fsc-gauge-tip__scale">
        {part(overview.parts.publicFiles, "Public files", 50)}
        {part(overview.parts.restrictUsers, "Restrict users", 30)}
        {part(overview.parts.orgWide, "Org-wide blocking", 20)}
      </div>

      {overview.parts.orgWide && (
        <p className="fsc-gauge-tip__why">
          {overview.parts.orgWide.usersWithPublic} of{" "}
          {overview.parts.orgWide.totalUsers} users have public files.
        </p>
      )}

      {overview.reasoning && (
        <p className="fsc-gauge-tip__why">{overview.reasoning}</p>
      )}
    </div>
  );

  const descText =
    overview.totalPending === 0
      ? "Every flagged item across your environment has been resolved."
      : overview.description;

  return (
    <div className="fsc-overview">
      <div className="fsc-overview__top">
        {/* Gauge Card */}
        <div className="fsc-overview__gauge-card">
          <div className="fsc-overview__gauge-head">
            RISK SCORE{" "}
            <Tooltip
              title={scoreExplainer}
              classes={gaugeInfoClasses}
              arrow
              placement="bottom-start"
              interactive
            >
              <i
                className="ti ti-info-circle fsc-overview__info-icon"
                tabIndex={0}
                role="button"
                aria-label="How the risk score is calculated"
              />
            </Tooltip>
          </div>

          <RiskGauge score={overview.score} />

          <div className="fsc-overview__score-row">
            <span className="fsc-overview__score">{overview.score}</span>
            <span className="fsc-overview__score-max">/{overview.maxScore}</span>
          </div>
        </div>

        {/* Info Card */}
        <div className="fsc-overview__info-card">
          <div className="fsc-overview__desc-card">
            <span
              className={`fsc-badge fsc-badge--${overview.tone} fsc-overview__risk-badge`}
            >
              {overview.riskLabel}
            </span>

            <p className="fsc-overview__desc-text">{descText}</p>

            {/* <p className="fsc-overview__desc-trend">
              {trend ? (
                <>
                  <i
                    className={`ti ${
                      trend.direction === "down"
                        ? "ti-arrow-down"
                        : "ti-arrow-up"
                    }`}
                  />
                  {trend.delta} points{" "}
                  {trend.direction === "down"
                    ? "lower than before"
                    : "higher than before"}
                </>
              ) : (
                overview.recommendation
              )}
            </p> */}
          </div>

          <div className="fsc-overview__stats-card">
            <div className="fsc-overview__stat">
              <i className="ti ti-shield-x fsc-overview__stat-icon fsc-overview__stat-icon--red" />

              <div>
                <p className="fsc-overview__stat-value">
                  {overview.bySeverity.Critical}
                </p>
                <p className="fsc-overview__stat-label">Critical Open</p>
              </div>
            </div>

            <div className="fsc-overview__stat">
              <i className="ti ti-shield-exclamation fsc-overview__stat-icon fsc-overview__stat-icon--amber" />

              <div>
                <p className="fsc-overview__stat-value">
                  {overview.bySeverity.High}
                </p>
                <p className="fsc-overview__stat-label">High Open</p>
              </div>
            </div>

            <div className="fsc-overview__stat">
              <i className="ti ti-shield-half fsc-overview__stat-icon fsc-overview__stat-icon--yellow" />

              <div>
                <p className="fsc-overview__stat-value">
                  {overview.bySeverity.Medium}
                </p>
                <p className="fsc-overview__stat-label">Medium Open</p>
              </div>
            </div>

            <div className="fsc-overview__stat">
              <i className="ti ti-shield-check fsc-overview__stat-icon fsc-overview__stat-icon--green" />

              <div>
                <p className="fsc-overview__stat-value">
                  {overview.bySeverity.Low}
                </p>
                <p className="fsc-overview__stat-label">Low Open</p>
              </div>
            </div>

            <div className="fsc-overview__stat">
              <i className="ti ti-shield fsc-overview__stat-icon fsc-overview__stat-icon--purple" />

              <div>
                <p className="fsc-overview__stat-value">
                  {overview.totalPending}
                </p>
                <p className="fsc-overview__stat-label">Total Risks Open</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}